import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SocialLinksState } from '../../types/network/socialLinksTypes';
import {
  fetchSocialLinksApi,
  addSocialLinkApi,
  deleteSocialLinkApi,
  fetchSocialMediaTypesApi,
} from '../../services/network/socialLinksApi';


const initialState: SocialLinksState = {
  links: [],
  types: [],
  loading: {
    fetchLinks: false,
    addLink: false,
    updateLink: false,
    deleteLink: false,
    fetchTypes: false,
  },
  error: null,
  successMessage: null,
};

// Thunk to fetch social links ------------------------------------------------
export const fetchSocialLinks = createAsyncThunk(
  'socialLinks/fetchSocialLinks',
  async ({ contentType, objectId }: { contentType: string; objectId: number }, { rejectWithValue }) => {
    try {
      const data = await fetchSocialLinksApi(contentType, objectId);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch social links.');
    }
  }
);

// Thunk to add a social link -------------------------------------------------
export const addSocialLink = createAsyncThunk(
  'socialLinks/addSocialLink',
  async (
    { social_media_type, link, content_type, object_id }: 
    { social_media_type: number; link: string; content_type: string; object_id: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await addSocialLinkApi({
        social_media_type,
        link,
        content_type,
        object_id,
      });
      await dispatch(fetchSocialLinks({ contentType: content_type, objectId: object_id }));
      await dispatch(getSocialMediaTypes());
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to add social link.');
    }
  }
);


// Thunk to delete a social link ----------------------------------------------
export const deleteSocialLink = createAsyncThunk(
  'socialLinks/deleteSocialLink',
  async (
    { linkId, contentType, objectId }: { linkId: number; contentType: string; objectId: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // حذف لینک
      const data = await deleteSocialLinkApi(linkId);

      // بارگذاری مجدد لینک‌ها
      await dispatch(fetchSocialLinks({ contentType, objectId }));
      await dispatch(getSocialMediaTypes());
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete social link.');
    }
  }
);


// Thunk to fetch social media types ------------------------------------------
export const getSocialMediaTypes = createAsyncThunk(
  'socialLinks/getSocialMediaTypes',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchSocialMediaTypesApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'Error fetching social media types.');
    }
  }
);

// Slice definition ----------------------------------------------------------
const socialLinksSlice = createSlice({
  name: 'socialLinks',
  initialState,
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch social links cases
      .addCase(fetchSocialLinks.pending, (state) => {
        state.loading.fetchLinks = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(fetchSocialLinks.fulfilled, (state, action) => {
        state.loading.fetchLinks = false;
        state.links = action.payload?.links || [];
        // state.successMessage = action.payload?.message || 'Social links loaded successfully.';
      })
      .addCase(fetchSocialLinks.rejected, (state, action) => {
        state.loading.fetchLinks = false;
        state.error = action.payload as string;
      })

      // Add social link cases
      .addCase(addSocialLink.pending, (state) => {
        state.loading.addLink = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addSocialLink.fulfilled, (state, action) => {
        state.loading.addLink = false;
        if (action.payload?.link) {
          state.links.push(action.payload.link);
        }
      })
      .addCase(addSocialLink.rejected, (state, action) => {
        state.loading.addLink = false;
        state.error = action.payload as string;
      })

      // Delete social link cases
      .addCase(deleteSocialLink.pending, (state) => {
        state.loading.deleteLink = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteSocialLink.fulfilled, (state, action) => {
        state.loading.deleteLink = false;
        state.links = state.links.filter((link) => link.id !== action.payload?.linkId);
        // state.successMessage = action.payload?.message || 'Social link deleted successfully---';
      })
      .addCase(deleteSocialLink.rejected, (state, action) => {
        state.loading.deleteLink = false;
        state.error = action.payload as string;
      })

      // Fetch social media types cases
      .addCase(getSocialMediaTypes.pending, (state) => {
        state.loading.fetchTypes = true;
        state.error = null;
      })
      .addCase(getSocialMediaTypes.fulfilled, (state, action) => {
        state.loading.fetchTypes = false;
        state.types = action.payload || [];
      })
      .addCase(getSocialMediaTypes.rejected, (state, action) => {
        state.loading.fetchTypes = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { clearMessages } = socialLinksSlice.actions;
export default socialLinksSlice.reducer;




